import { useMemo, useState } from "react";
import { Store, ShoppingCart } from "lucide-react";
import { getPalette, getScreenshot } from "./TakedownCard";
import {
  Chip,
  RecommendedActionChip,
  formatRecallDate,
} from "../../Universal/takedown/takedownUtils";

/* Feed card for a vendor listing ("takedown_shopping") — one store caught still
   selling a flagged product. Mirrors TakedownCard's frame/palette, but the headline
   is the enforcement status: the action this vendor needs (a first warning letter, or
   a second one if it was already warned and hasn't complied). Display-only; the
   MainFeeds wrapper handles click-to-open (opens the shopping detail view). */

// Flatten the shopping doc into the fields the card renders. Top-level wins; tolerate a
// meta-nested doc too (mirrors TakedownCard.getTakedown / TakedownDetailView).
const getShopping = (article = {}) => {
  const src = { ...(article.meta || {}), ...article };
  return {
    merchant: src.merchant || src.source || "",
    price: src.price || "",
    title: src.title || "Vendor listing",
    recommendedAction: src.recommended_action || null,
    product: src.takedown_product || "",
    subject: src.takedown_subject || "",
    dateValue: src.date || src.date_published || null,
  };
};

export const TakedownShoppingCard = ({ article, t }) => {
  const p = useMemo(() => getPalette(t), [t]);
  const data = useMemo(() => getShopping(article || {}), [article]);
  const [broken, setBroken] = useState(false);

  const screenshot = getScreenshot(article || {});
  const showScreenshot = Boolean(screenshot) && !broken;
  const { absolute: dateLabel } = formatRecallDate(data.dateValue);

  return (
    <article
      className="relative self-start overflow-hidden rounded-2xl"
      style={{
        background: p.cardBg,
        border: `1px solid ${p.cardBorder}`,
        boxShadow: p.cardShadow,
      }}
    >
      <div className="flex h-full flex-col">
        {showScreenshot && (
          <div
            className="relative aspect-[16/9] w-full overflow-hidden border-b"
            style={{ borderColor: p.divider }}
          >
            <img
              src={screenshot}
              alt=""
              className="h-full w-full object-cover object-top"
              onError={() => setBroken(true)}
            />
            <div
              className="pointer-events-none absolute inset-x-0 bottom-0 h-28"
              style={{ background: p.imageGradient }}
            />
          </div>
        )}

        <div className="flex flex-1 flex-col gap-3 p-4">
          {/* Headline: For Sale + the enforcement action this vendor needs */}
          <div className="flex flex-wrap items-center gap-1.5">
            <Chip color="#f97316">
              <ShoppingCart size={10} className="shrink-0" />
              For Sale
            </Chip>
            <RecommendedActionChip action={data.recommendedAction} />
            {dateLabel && (
              <span className="ml-auto text-[10px]" style={{ color: p.textMuted }}>
                {dateLabel}
              </span>
            )}
          </div>

          {/* Vendor + price */}
          <div className="flex items-center gap-1.5 min-w-0">
            <Store
              size={12}
              className="shrink-0"
              style={{ color: p.textMuted }}
              strokeWidth={1.75}
            />
            <span
              className="truncate text-[12px] font-medium"
              style={{ color: p.textSecondary }}
            >
              {data.merchant || "Vendor"}
            </span>
            {data.price && (
              <span
                className="shrink-0 text-[11px]"
                style={{ color: p.textMuted }}
              >
                · {data.price}
              </span>
            )}
          </div>

          {/* Listing title */}
          {data.title && (
            <h3
              className="text-[15px] font-semibold leading-snug line-clamp-2"
              style={{ color: p.textPrimary }}
            >
              {data.title}
            </h3>
          )}

          {/* Parent flagged product + subject — what/why at a glance */}
          {(data.product || data.subject) && (
            <div className="flex flex-col gap-0.5">
              {data.product && (
                <div className="text-[11px]" style={{ color: p.textSecondary }}>
                  <span style={{ color: p.textMuted }}>Flagged: </span>
                  {data.product}
                </div>
              )}
              {data.subject && (
                <p
                  className="text-[12px] leading-relaxed line-clamp-2"
                  style={{ color: p.textMuted }}
                >
                  {data.subject}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </article>
  );
};

export default TakedownShoppingCard;
